/*
 * Desconto automático da calculadora (card t_5ff66cff), consultando a
 * Storefront API pública da Shopify (sem token) via cartCreate.
 *
 * Roda de dois jeitos, sem import/export de módulo:
 *   - no tema, como <script>: fica em window.WillCalculadoraDesconto;
 *   - no Node, para teste: require('assets/will-calculadora-desconto.js').
 *
 * REGRA DO GUS (25/09/2026): nenhuma regra de desconto — número fixo tipo 5%,
 * R$ 300, R$ 5 — mora neste arquivo. O valor do desconto e o critério de
 * quando ele se aplica vêm SEMPRE da resposta da Shopify
 * (discountAllocations); este módulo só soma e devolve o que a API
 * respondeu, sem decidir nada sozinho. O(s) nome(s) do desconto (`titulo`) é
 * exatamente o `title`/`code` que a Shopify devolveu — sem reescrever, sem
 * traduzir.
 *
 * A query pede discountAllocations em DOIS lugares: dentro de cada linha
 * (`cart.lines.edges[].node.discountAllocations`, desconto por PRODUTO, ex.:
 * "R$ 5 na Massa Corrida 20 KG") e no CARRINHO inteiro
 * (`cart.discountAllocations`, desconto por PEDIDO, ex.: "5% acima de
 * R$ 300"). Confirmado ao vivo em 25/09/2026: um desconto por pedido reduz
 * cart.cost.totalAmount mas NÃO aparece em discountAllocations de nenhuma
 * linha — só no nível do carrinho. Sem o campo no carrinho, a tela erraria
 * justamente o desconto de R$300 citado no card.
 *
 * A conta de rendimento (assets/will-calculadora-motor.js) decide a
 * combinação mais barata pelo PREÇO DE TABELA e não muda. Este módulo é uma
 * camada ortogonal: depois que o motor decidiu a combinação, consultarDesconto
 * pergunta à Shopify qual é o preço FINAL daquela combinação exata (o mesmo
 * preço que o carrinho de verdade cobraria), sem tocar no carrinho do
 * cliente — cartCreate cria um carrinho À PARTE, só para a consulta.
 *
 * REDE (card t_3821bce8): a chamada NÃO usa o fetch da janela principal. O
 * web-pixels-manager da Shopify envolve window.fetch/XMLHttpRequest da página
 * e trata qualquer corpo parecido com cartCreate como "adicionou ao
 * carrinho", disparando o evento product_added_to_cart para os pixels da loja
 * mesmo sem o carrinho real do visitante mudar. Por isso o fetch roda dentro
 * de um Web Worker (criado a partir de um Blob): o worker tem escopo global
 * próprio, fora do alcance do wrapper que só envolve window/document da
 * página principal. Correlaciona pedido/resposta por id incremental (podem
 * chegar fora de ordem com 2 consultas simultâneas); timeout de 3s local no
 * thread principal, sem depender de AbortController dentro do worker.
 *
 * Parte pura (testável sem mockar fetch nem Worker): chaveCache,
 * paraLinhasGid, corpoMutacao, interpretarResposta — interpretarResposta roda
 * sempre no thread principal, sobre o JSON que o worker devolveu por
 * postMessage. Parte de rede: consultarDesconto, com cache em memória por
 * combinação de itens. Se Worker/Blob/URL.createObjectURL não existirem no
 * navegador, resolve { ok: false } direto — nunca cai para fetch da janela
 * principal, porque isso reintroduziria o evento de pixel.
 */
var WillCalculadoraDesconto = (function () {
  'use strict';

  var VERSAO_API = '2026-07';
  var TIMEOUT_MS = 3000;
  var PREFIXO_GID_VARIANTE = 'gid://shopify/ProductVariant/';

  var MUTACAO_CART_CREATE =
    'mutation($lines: [CartLineInput!]!) {\n' +
    '  cartCreate(input: {lines: $lines}) {\n' +
    '    cart {\n' +
    '      cost { subtotalAmount { amount } totalAmount { amount } }\n' +
    '      discountAllocations {\n' +
    '        discountedAmount { amount }\n' +
    '        ... on CartCodeDiscountAllocation { code }\n' +
    '        ... on CartAutomaticDiscountAllocation { title }\n' +
    '        ... on CartCustomDiscountAllocation { title }\n' +
    '      }\n' +
    '      lines(first: 20) {\n' +
    '        edges {\n' +
    '          node {\n' +
    '            quantity\n' +
    '            cost { totalAmount { amount } }\n' +
    '            discountAllocations {\n' +
    '              discountedAmount { amount }\n' +
    '              ... on CartCodeDiscountAllocation { code }\n' +
    '              ... on CartAutomaticDiscountAllocation { title }\n' +
    '              ... on CartCustomDiscountAllocation { title }\n' +
    '            }\n' +
    '          }\n' +
    '        }\n' +
    '      }\n' +
    '    }\n' +
    '    userErrors { field message }\n' +
    '  }\n' +
    '}';

  // "49.90" | 49.9 -> 4990 centavos. Formato inesperado -> null (sem exceção).
  function paraCentavos(valor) {
    if (typeof valor !== 'string' && typeof valor !== 'number') return null;
    var n = Number(valor);
    if (!isFinite(n)) return null;
    return Math.round(n * 100);
  }

  /*
   * Chave de cache a partir da lista de itens {merchandiseId, quantity}: a
   * mesma combinação em ordem diferente tem que gerar a MESMA chave, por
   * isso ordena por merchandiseId antes de serializar.
   */
  function chaveCache(itens) {
    var lista = (itens || []).slice().sort(function (a, b) {
      var idA = String(a && a.merchandiseId);
      var idB = String(b && b.merchandiseId);
      if (idA < idB) return -1;
      if (idA > idB) return 1;
      return 0;
    });
    return lista
      .map(function (i) { return String(i && i.merchandiseId) + 'x' + String(i && i.quantity); })
      .join('|');
  }

  // [{ id, quantity }] (formato de itensDoCarrinho, id numérico da variante)
  // -> [{ merchandiseId: 'gid://shopify/ProductVariant/<id>', quantity }].
  function paraLinhasGid(itensCarrinho) {
    return (itensCarrinho || []).map(function (i) {
      return { merchandiseId: PREFIXO_GID_VARIANTE + i.id, quantity: i.quantity };
    });
  }

  // Corpo da mutação GraphQL a partir da lista de itens {merchandiseId, quantity}.
  function corpoMutacao(itens) {
    var lines = (itens || []).map(function (i) {
      return { merchandiseId: i.merchandiseId, quantity: i.quantity };
    });
    return { query: MUTACAO_CART_CREATE, variables: { lines: lines } };
  }

  /*
   * Interpreta a resposta JSON da API. Sucesso:
   *   { ok: true, totalCentavos, descontos: [{ titulo, valorCentavos }] }
   * Qualquer formato inesperado, erro de rede já tratado pelo chamador, ou
   * userErrors não vazio: { ok: false }. Nunca lança exceção.
   */
  function interpretarResposta(json) {
    try {
      var corpo = json && json.data && json.data.cartCreate;
      if (!corpo || typeof corpo !== 'object') return { ok: false };
      if (Array.isArray(corpo.userErrors) && corpo.userErrors.length > 0) return { ok: false };

      var cart = corpo.cart;
      var totalCentavos = paraCentavos(cart && cart.cost && cart.cost.totalAmount && cart.cost.totalAmount.amount);
      if (totalCentavos === null) return { ok: false };

      var descontos = [];

      function coletar(alocacoes) {
        if (!Array.isArray(alocacoes)) return;
        alocacoes.forEach(function (d) {
          var valorCentavos = paraCentavos(d && d.discountedAmount && d.discountedAmount.amount);
          if (valorCentavos === null) return;
          var titulo = (d && (d.title || d.code)) || '';
          if (!titulo) return;
          descontos.push({ titulo: titulo, valorCentavos: valorCentavos });
        });
      }

      // Desconto de PEDIDO (ex.: "5% acima de R$ 300"): no carrinho, não na linha.
      coletar(cart.discountAllocations);

      // Desconto de PRODUTO (ex.: "R$ 5 na Massa Corrida 20 KG"): por linha.
      var edges = (cart.lines && cart.lines.edges) || [];
      if (!Array.isArray(edges)) return { ok: false };
      edges.forEach(function (edge) {
        coletar(edge && edge.node && edge.node.discountAllocations);
      });

      /*
       * C2 (revisão t_6205af6c): com 2+ linhas no carrinho, um desconto de
       * PEDIDO (ex.: "5% acima de R$ 300") pode voltar mais de uma vez em
       * cart.discountAllocations — uma alocação por linha — e cada uma
       * carrega o MESMO título. Sem isso o texto duplicava ("5% acima de
       * R$ 300, 5% acima de R$ 300"). Dedup por título, somando
       * valorCentavos das entradas repetidas: o total exibido continua
       * batendo com o desconto real, só o texto para de repetir. Descontos
       * DIFERENTES (títulos diferentes) continuam todos na lista.
       */
      var porTitulo = {};
      var ordemTitulos = [];
      descontos.forEach(function (d) {
        if (!Object.prototype.hasOwnProperty.call(porTitulo, d.titulo)) {
          porTitulo[d.titulo] = { titulo: d.titulo, valorCentavos: 0 };
          ordemTitulos.push(d.titulo);
        }
        porTitulo[d.titulo].valorCentavos += d.valorCentavos;
      });
      var descontosSemDuplicata = ordemTitulos.map(function (titulo) { return porTitulo[titulo]; });

      return { ok: true, totalCentavos: totalCentavos, descontos: descontosSemDuplicata };
    } catch (erro) {
      return { ok: false };
    }
  }

  // Cache em memória: mesma chave não repete a consulta de rede.
  var cache = {};

  /*
   * Código-fonte do Web Worker, como string (vira Blob depois). Escopo global
   * PRÓPRIO do worker — não é `window` — por isso o wrapper de pixels da
   * Shopify (que só envolve o fetch/XHR da página principal) não vê esta
   * chamada. Recebe { id, url, body } e devolve { id, ok, json } por
   * postMessage; nunca lança para fora do worker.
   */
  var CODIGO_WORKER =
    'self.onmessage = function (ev) {\n' +
    '  var id = ev.data.id;\n' +
    '  fetch(ev.data.url, {\n' +
    '    method: "POST",\n' +
    '    headers: { "Content-Type": "application/json" },\n' +
    '    body: ev.data.body\n' +
    '  })\n' +
    '    .then(function (resp) {\n' +
    '      if (!resp.ok) { self.postMessage({ id: id, ok: false }); return null; }\n' +
    '      return resp.json().then(function (json) {\n' +
    '        self.postMessage({ id: id, ok: true, json: json });\n' +
    '      });\n' +
    '    })\n' +
    '    .catch(function () { self.postMessage({ id: id, ok: false }); });\n' +
    '};';

  var worker = null; // lazy, criado na primeira consultarDesconto
  var pendentes = {}; // id -> { resolve }
  var proximoId = 0;

  function suportaWorker() {
    return typeof Worker !== 'undefined' && typeof Blob !== 'undefined' &&
      typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';
  }

  // Resolve (uma única vez) todas as consultas pendentes como falha — usado
  // quando o worker inteiro quebra (ev. erro de execução, CSP bloqueando).
  function falharPendentes() {
    for (var id in pendentes) {
      if (Object.prototype.hasOwnProperty.call(pendentes, id)) {
        var pendente = pendentes[id];
        delete pendentes[id];
        pendente.resolve({ ok: false });
      }
    }
  }

  function obterWorker() {
    if (worker) return worker;
    var blob = new Blob([CODIGO_WORKER], { type: 'application/javascript' });
    var url = URL.createObjectURL(blob);
    worker = new Worker(url);
    worker.onmessage = function (ev) {
      var dado = ev && ev.data;
      var id = dado && dado.id;
      var pendente = pendentes[id];
      delete pendentes[id];
      if (!pendente) return; // já resolvido por timeout
      if (!dado.ok) { pendente.resolve({ ok: false }); return; }
      pendente.resolve(interpretarResposta(dado.json));
    };
    worker.onerror = falharPendentes;
    return worker;
  }

  /*
   * Consulta a Shopify para o preço final de `itens` ([{merchandiseId, quantity}]),
   * via Web Worker (nunca via fetch da janela principal — ver comentário do
   * módulo). Devolve { ok: false } se o navegador não suportar Worker/Blob, se
   * o worker não responder em 3s, se a rede falhar, a resposta não for OK, ou
   * o GraphQL vier com userErrors — nunca lança exceção, nunca trava a tela.
   */
  function consultarDesconto(itens) {
    var chave = chaveCache(itens);
    if (Object.prototype.hasOwnProperty.call(cache, chave)) return cache[chave];

    var promessa = new Promise(function (resolve) {
      if (!suportaWorker()) { resolve({ ok: false }); return; }

      var w;
      try {
        w = obterWorker();
      } catch (erro) {
        resolve({ ok: false });
        return;
      }

      var id = proximoId++;
      var resolvido = false;
      var timer = setTimeout(function () {
        if (resolvido) return;
        resolvido = true;
        delete pendentes[id];
        resolve({ ok: false });
      }, TIMEOUT_MS);

      pendentes[id] = {
        resolve: function (resultado) {
          if (resolvido) return;
          resolvido = true;
          clearTimeout(timer);
          resolve(resultado);
        },
      };

      try {
        var url = window.location.origin + '/api/' + VERSAO_API + '/graphql.json';
        w.postMessage({ id: id, url: url, body: JSON.stringify(corpoMutacao(itens)) });
      } catch (erro) {
        var pendente = pendentes[id];
        delete pendentes[id];
        if (pendente) pendente.resolve({ ok: false });
      }
    });

    cache[chave] = promessa;
    return promessa;
  }

  return {
    chaveCache: chaveCache,
    paraLinhasGid: paraLinhasGid,
    corpoMutacao: corpoMutacao,
    interpretarResposta: interpretarResposta,
    consultarDesconto: consultarDesconto,
  };
})();

if (typeof module !== 'undefined' && module.exports) module.exports = WillCalculadoraDesconto;
